/**
 * UranIA PA — Cutover announcement banner (US-008 Fase 6 AC#1).
 *
 * Banner sticky che soddisfa il requisito letterale del PRD Fase 6
 * §6.2: "Notifica in-app 7 giorni prima del cutover finale; banner
 * sticky 'Cutover in {N} giorni — pianifica il tuo lavoro'".
 *
 * Comportamento (AC#1 letterale):
 *   - Sticky topbar sotto il banner AI Act art. 50 (`position: sticky;
 *     top: 0; z-index: 1080` come AiDisclosureBanner; lo z-index resta
 *     coerente perché entrambi sono sticky-in-flow nel layout root).
 *   - Visibile **sse**:
 *       * l'utente è autenticato e ha un `tenantId` coerente con la
 *         tabella `CUTOVER_SCHEDULE` (Fase 6: solo `tenant_2adg`);
 *       * `daysUntilCutover ∈ [0, 7]` (US-008 AC#1 letterale "starts
 *         7 days before");
 *       * il flag `dismissed` non è ancora stato attivato in questa
 *         sessione (dismiss volatile, riappare al refresh).
 *   - Click dismiss loggato come evento audit
 *     `CutoverAnnouncementDismissed` via `recordUiAuditEvent` (US-F1-11
 *     tabella `audit_events`). Il transport è fire-and-forget.
 *   - Click sull'area banner emette evento audit dedicato
 *     `CutoverBannerClicked{tenant_id, days_until, view, ts,
 *     user_id_hash}` per il backend di compliance AgID (P.8 disclosure
 *     trasparente). Pattern coerente con AiDisclosureBanner.
 *
 * Testo (AC#1 letterale):
 *   - i18n key `cutover_announcement.banner` (italiano primario
 *     `it-IT`, inglese fallback `en-EU`).
 *   - Il template parla in italiano-prima: "Cutover in {{days}} giorni
 *     — pianifica il tuo lavoro" + sub-label con finestra letterale +
 *     CTA "Apri la status page" verso `entry.statusPageUrl`.
 *
 * A11y:
 *   - `role="region"` con `aria-label` esplicito (region-named
 *     landmark).
 *   - `role="alert"` evitato per non spammare lo screen reader ad ogni
 *     navigazione client-side; `aria-live="polite"` WCAG 2.1 §1.3.1.
 *   - Bottone dismiss con `aria-label` (icona × decorativa).
 *   - Contrasto testo/sfondo ≥ 4.5:1 (palette AgID Bootstrap Italia
 *     `--bs-info` su nero — verificato da axe-core WCAG2 AA in CI
 *     quando il banner sarà incluso nello smoke axe-core Fase 7).
 */

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

import { recordUiAuditEvent } from '../../lib/audit/audit-client.ts';
import { computeUserIdHash } from '../../lib/audit/user-id-hash.ts';
import { selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import type { UraniaPrincipal } from '../../lib/auth/auth-store.ts';
import {
  selectCutoverAnnouncementDismissed,
  useCutoverAnnouncementBannerStore,
} from '../../lib/cutover/announcement-store.ts';
import {
  resolveActiveCutoverForTenant,
  type CutoverScheduleEntry,
} from '../../lib/cutover/schedule-config.ts';

function actorFromPrincipal(principal: UraniaPrincipal | null): {
  readonly subject: string | null;
  readonly tenantId: string | null;
  readonly roles: readonly string[];
} {
  return {
    subject: principal?.subject ?? null,
    tenantId: principal?.tenantId ?? null,
    roles: principal?.roles ?? [],
  };
}

function currentView(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

/**
 * Override-able clock per i test (e per dev demo). In produzione
 * `() => new Date()` viene risolto al render — il banner non si
 * "anima" da solo: cambia tile solo al re-render della route (che
 * avviene almeno una volta per sessione). Una refresh periodica
 * client-side non è necessaria perché il decremento di "N giorni"
 * succede una volta al giorno e l'utente apre la SPA almeno una volta
 * al giorno nei 7 gg pre-cutover.
 */
export interface CutoverAnnouncementBannerProps {
  readonly clock?: () => Date;
}

export function CutoverAnnouncementBanner({
  clock = () => new Date(),
}: CutoverAnnouncementBannerProps = {}): JSX.Element | null {
  const { t } = useTranslation();
  const principal = useAuthStore(selectPrincipal);
  const dismissed = useCutoverAnnouncementBannerStore(selectCutoverAnnouncementDismissed);
  const dismiss = useCutoverAnnouncementBannerStore((s) => s.dismiss);

  const active = useMemo(
    () => resolveActiveCutoverForTenant(principal?.tenantId ?? null, clock()),
    [principal?.tenantId, clock],
  );

  const emitBannerClicked = useCallback(
    async (entry: CutoverScheduleEntry, daysUntil: number): Promise<void> => {
      const userIdHash = await computeUserIdHash(principal?.subject ?? null);
      void recordUiAuditEvent({
        action: 'CutoverBannerClicked',
        actor: actorFromPrincipal(principal),
        ts: new Date().toISOString(),
        attrs: {
          tenant_id: entry.tenantId,
          cutover_at: entry.cutoverAtIso,
          days_until: daysUntil,
          view: currentView(),
          user_id_hash: userIdHash,
          locale: typeof document === 'undefined' ? '' : document.documentElement.lang,
        },
      });
    },
    [principal],
  );

  const handleBannerClick = useCallback((): void => {
    if (active === null) {
      return;
    }
    void emitBannerClicked(active.entry, active.daysUntil);
  }, [active, emitBannerClicked]);

  const handleDismiss = useCallback((): void => {
    if (active !== null) {
      void recordUiAuditEvent({
        action: 'CutoverAnnouncementDismissed',
        actor: actorFromPrincipal(principal),
        ts: new Date().toISOString(),
        attrs: {
          tenant_id: active.entry.tenantId,
          cutover_at: active.entry.cutoverAtIso,
          days_until: active.daysUntil,
          path: currentView(),
          locale: typeof document === 'undefined' ? '' : document.documentElement.lang,
        },
      });
    }
    dismiss();
  }, [active, dismiss, principal]);

  if (active === null || dismissed) {
    return null;
  }

  const { entry, daysUntil } = active;
  const headingKey =
    daysUntil === 0
      ? 'cutover_announcement.heading_today'
      : daysUntil === 1
        ? 'cutover_announcement.heading_tomorrow'
        : 'cutover_announcement.heading_n_days';

  return (
    <aside
      className="urania-cutover-banner alert alert-info rounded-0 m-0 py-2 px-3 small d-flex align-items-start gap-3"
      role="region"
      aria-label={t('cutover_announcement.region_label')}
      aria-live="polite"
      data-testid="cutover-announcement-banner"
      data-tenant-id={entry.tenantId}
      data-days-until={daysUntil}
      onClick={handleBannerClick}
    >
      <div className="urania-cutover-banner__body flex-grow-1">
        <strong>{t(headingKey, { days: daysUntil })}</strong>{' '}
        <span>
          {t('cutover_announcement.window_label', {
            window: entry.cutoverWindowLabel,
          })}
        </span>{' '}
        <a
          className="urania-cutover-banner__link"
          href={entry.statusPageUrl}
          rel="noreferrer noopener external"
          target="_blank"
          data-testid="cutover-announcement-status-page-link"
        >
          {t('cutover_announcement.status_page_link')}
        </a>
      </div>
      <button
        type="button"
        className="btn-close urania-cutover-banner__dismiss"
        aria-label={t('cutover_announcement.dismiss_aria')}
        data-testid="cutover-announcement-banner-dismiss"
        onClick={handleDismiss}
      />
    </aside>
  );
}
